import type { Position } from './position.types';

/**
 * Blocco di contenuto "editoriale" traducibile.
 *
 * Nota: `id` è un identificatore stabile (utile per migrazioni/ordini/patch). Non è legato al tipo POI.
 */
export interface POIDetail {
    id: string;
    label: Record<string, string>;
    text: Record<string, string>;
}

/**
 * Formato canonico per gli orari di apertura (pensato per un componente di data-entry dedicato).
 *
 * Nota:
 * - al momento in `POIMetaMap` `openingHours` è ancora una stringa per compatibilità.
 * - TODO: migrare `openingHours` a questo tipo quando avremo una UI/formatter condivisi.
 */
export type OpeningHoursDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface OpeningHoursInterval {
    start: string; // "HH:mm"
    end: string; // "HH:mm"
}

export interface OpeningHoursWeeklyRule {
    days: OpeningHoursDay[];
    intervals: OpeningHoursInterval[];
}

export interface OpeningHoursException {
    date: string; // "YYYY-MM-DD"
    closed?: boolean;
    intervals?: OpeningHoursInterval[];
    note?: string;
}

export interface OpeningHours {
    timezone?: string; // es. "Europe/Rome"
    weekly: OpeningHoursWeeklyRule[];
    exceptions?: OpeningHoursException[];
    note?: string;
}

/**
 * Registry dei meta per type.
 *
 * Perché esiste:
 * - `details[]` copre la parte di visualizzazione (label+testo multi-lingua) ed è volutamente elastica.
 * - `meta` invece è pensato per "contratti" (logiche, API future, algoritmi, automazioni) e quindi lo tipizziamo.
 *
 * Come mantenerlo sano:
 * - aggiungi/rimuovi un type qui SOLO se esiste anche in `POI_CATEGORIES`.
 * - se un type non ha meta, usa `null` (contratto esplicito) invece di `{}`.
 *
 * Nota: in `shared/constants/poiCategories.ts` c'è un assert compile-time che obbliga l'allineamento
 * tra type presenti in categorie e `POIType`.
 */
export interface POIMetaMap {
    // ─── Informazioni & Accessi ─────────────────────────
    info: {
        // (String): Per mostrare un numero di telefono da chiamare per informazioni.
        phoneNumber?: string;
        // (String): Per mostrare un link o far partire una chiamata verso un ufficio.
        bookingAction?: string;
        // (String, TODO→OpeningHours): per ora è testo libero; in futuro verrà gestito via UI dedicata.
        openingHours?: string;
    };
    ticket: {
        // (String, TODO→OpeningHours): serve per calcolare e mostrare il badge "Aperto/Chiuso".
        openingHours?: string;
        // (String): Per mostrare un link o far partire una chiamata verso un ufficio.
        bookingAction?: string;
    };
    meeting_point: null;

    // ─── Sicurezza & Emergenze ──────────────────────────
    first_aid: {
        // (String): Per mostrare un numero di telefono da chiamare in caso di necessità.
        phoneNumber?: string;
        // (String, TODO→OpeningHours): usato per badge "Aperto/Chiuso" e/o regole di disponibilità.
        openingHours?: string;
    };
    danger: {
        // Questo fa lampeggiare un indicatore visivo in caso di emergenza.
        emergencyLevel?: 'Low' | 'High';
    };

    // ─── Servizi di Base ────────────────────────────────
    restroom: {
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare "Pulito 10 min fa").
        updatedAt?: number;
        // Per chiudere virtualmente una fontanella o un bagno guasto.
        isOperational?: boolean;
    };
    water: {
        // Per chiudere virtualmente una fontanella o un bagno guasto.
        isOperational?: boolean;
    };
    shower: {
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare "Pulito 10 min fa").
        updatedAt?: number;
        // Per chiudere virtualmente una fontanella o un bagno guasto.
        isOperational?: boolean;
    };

    waste: null;

    // ─── Ristoro ───────────────────────────────────────
    restaurant: {
        // (Number): Da aggiornare via gateway/IoT per mostrare "80% pieno".
        currentOccupancy?: number;
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare all'utente "Rilevato 10 min fa").
        updatedAt?: number;
        // (Number): La capacità totale del ristorante, per mostrare "80% pieno".
        capacity?: number;
        // (String): Per mostrare un link al menu online o far partire una prenotazione.
        bookingAction?: string;
        // (String): Per mostrare un numero di telefono da chiamare per informazioni o prenotazioni.
        phoneNumber?: string;
    };
    bar: null;
    vending: null;

    // ─── Campeggio ─────────────────────────────────────
    camping: null;
    pitch: {
        // (Number): Il limite massimo.
        capacity?: number;
        // Da aggiornare via gateway/IoT per mostrare "80% pieno".
        currentOccupancy?: number;
    };

    // ─── Giochi & Avventura ────────────────────────────
    playground: {
        // Per mostrare un badge "Tempo di attesa: 20 min"
        waitingTime?: string;
    };
    swimming_pool: {
        // Per mostrare quante persone può contenere la piscina
        capacity?: number;
        // Da aggiornare via gateway/IoT per mostrare "80% pieno".
        currentOccupancy?: number;
        // (Number): Per mostrare temperatura dell'acqua.
        temperature?: number;
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare all'utente "Rilevato 10 min fa").
        updatedAt?: number;
    };
    adventure: {
        // Per mostrare un badge "Tempo di attesa: 20 min"
        waitingTime?: string;
    };
    sport_field: {
        // indica se il campo è attualmente occupato (utile per campi da sport).
        isOccupied?: boolean;
    };

    // ─── Cultura & Archeologia ─────────────────────────
    monument: {
        // (String): Per far partire una prenotazione o un'azione correlata.
        bookingAction?: string;
        // (String): Per mostrare un numero di telefono da chiamare per informazioni.
        phoneNumber?: string;
    };
    viewpoint: null;

    // ─── Natura & Percorsi ─────────────────────────────
    trail: null;
    beach: {
        // Per mostrare quante persone può contenere la spiaggia
        capacity?: number;
        // Da aggiornare via gateway/IoT per mostrare "80% pieno".
        currentOccupancy?: number;
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare all'utente "Rilevato 10 min fa").
        updatedAt?: number;
    };
    water_point: null;
    nature_point: null;

    // ─── Mobilità ──────────────────────────────────────
    parking: {
        // (Number): Il limite massimo.
        capacity?: number;
        // Da aggiornare via gateway/IoT per mostrare "80% pieno".
        currentOccupancy?: number;
        // indica se ci sono ancora piazzole disponibili.
        isOccupied?: boolean;
        // Per mostrare un'icona di ricarica elettrica o filtrare i parcheggi in base a questo requisito.
        hasElectricCharging?: boolean;
        // timestamp in millisecondi dell'ultimo aggiornamento (utile per notifiche allo staff o per mostrare all'utente "Rilevato 10 min fa").
        updatedAt?: number;
    };
    shuttle: {
        // (String[]): Per mostrare gli orari di partenza della navetta.
        lastDepartureTimes?: string[];
        // (String): Per far apparire il tasto "Chiama ora" nel popup della mappa.
        phoneNumber?: string;
    };

    // ─── Regole & Accessibilità ────────────────────────
    quiet_area: null;
    smoking_area: null;
}

/**
 * Tutti i tipi POI supportati (derivati dal registry `POIMetaMap`).
 */
export type POIType = keyof POIMetaMap;

export interface POICategory<TType extends string = POIType> {
    /** Identificatore stabile per styling/logica (es. "info", "safety"). */
    id: string;
    label: string;
    icon: string;
    symbol: string;
    types: { id: TType; label: string; icon: string, symbol: string }[];
}

/**
 * Utility per ottenere il tipo `meta` dato un `POIType`.
 */
export type POIMetaOf<T extends POIType> = POIMetaMap[T];

/**
 * Sottoinsieme di POIType che hanno `meta` come oggetto (non null).
 */
export type POITypeWithObjectMeta = {
    [K in POIType]: POIMetaMap[K] extends null ? never : K;
}[POIType];

/**
 * Lista runtime dei type con meta oggetto.
 *
 * Nota: questa lista DEVE restare allineata a `POIMetaMap`.
 * È usata a runtime (mapper/save) per decidere se forzare `meta: null`.
 */
export const POI_OBJECT_META_TYPES = [
    'info',
    'ticket',
    'first_aid',
    'danger',
    'restroom',
    'water',
    'shower',
    'restaurant',
    'pitch',
    'playground',
    'swimming_pool',
    'adventure',
    'sport_field',
    'monument',
    'parking',
    'shuttle',
    'beach'
] as const satisfies readonly POITypeWithObjectMeta[];

// Assert compile-time: la lista runtime deve coprire ESATTAMENTE tutti i type con meta oggetto.
// Se aggiungi/rimuovi un meta-object in `POIMetaMap`, TypeScript ti obbligherà ad aggiornare questa lista.
type Assert<T extends true> = T;
type IsExact<A, B> =
    [Exclude<A, B>] extends [never]
    ? ([Exclude<B, A>] extends [never] ? true : false)
    : false;

type POIObjectMetaTypeFromList = (typeof POI_OBJECT_META_TYPES)[number];
type _assertPOIObjectMetaListMatchesRegistry = Assert<IsExact<POIObjectMetaTypeFromList, POITypeWithObjectMeta>>;

export const isPOIObjectMetaType = (type: POIType): type is POITypeWithObjectMeta => {
    return (POI_OBJECT_META_TYPES as readonly string[]).includes(type);
};

/**
 * Type-guard usato nei mapper/save: ritorna true quando per contratto vogliamo `meta: null`.
 */
export const isPOINullMetaType = (type: POIType): type is Exclude<POIType, POITypeWithObjectMeta> => {
    return !isPOIObjectMetaType(type);
};

/**
 * Base POI: campi comuni a tutti i punti.
 *
 * Nota su `details[]`:
 * - è la parte "editoriale" e traducibile, pensata per UI e contenuto flessibile.
 * - non sostituisce `meta`: i meta restano il posto giusto per contratti/integrazioni/logiche.
 */
export interface BasePOI {
    id: string;
    name: Record<string, string>; // Traduzioni: { "it": "Nome", "en": "Name", "fr": "Nom" }
    position: Position;
    icon?: string;
    description?: Record<string, string>; // Traduzioni: { "it": "Descrizione", "en": "Description" }
    details?: POIDetail[]; // Blocchi aggiuntivi traducibili (label + text)
    distance?: number;
    isSpoke?: boolean; // identifica dispositivo spoke
    spokeId?: number; // se è uno spoke, memorizza il suo identificatore univoco
}

// Discriminated union per POI con meta tipizzati in base al type
export type POI = {
    [K in POIType]: BasePOI & {
        type: K;
        meta: POIMetaMap[K];
    }
}[POIType];
